export default function PrivacyPolicy() {
  const G = "#0077B5";
  const style = {
    body: { background: "#fff", minHeight: "100vh", fontFamily: "-apple-system,'Segoe UI',Helvetica,sans-serif" },
    wrap: { maxWidth: 800, margin: "0 auto", padding: "48px 24px 80px" },
    logo: { fontFamily: "'Playfair Display',Georgia,serif", fontSize: 28, fontWeight: 700, color: G, marginBottom: 40, display: "block", textDecoration: "none" },
    h1: { fontFamily: "'Playfair Display',Georgia,serif", fontSize: 32, fontWeight: 700, color: "#1A1A1A", marginBottom: 8, marginTop: 0 },
    updated: { fontSize: 13, color: "#888", marginBottom: 48 },
    h2: { fontFamily: "'Playfair Display',Georgia,serif", fontSize: 20, fontWeight: 700, color: "#1A1A1A", marginTop: 44, marginBottom: 12, borderBottom: "2px solid #F3F2EF", paddingBottom: 8 },
    h3: { fontSize: 15, fontWeight: 700, color: "#1A1A1A", marginTop: 20, marginBottom: 6 },
    p: { fontSize: 15, lineHeight: 1.8, color: "#333", marginBottom: 16 },
    ul: { fontSize: 15, lineHeight: 1.8, color: "#333", marginBottom: 16, paddingLeft: 24 },
    footer: { borderTop: "1px solid #E1E9EE", marginTop: 64, paddingTop: 20, textAlign: "center", fontSize: 13, color: "#aaa" },
    link: { color: G },
  };

  return (
    <div style={style.body}>
      <div style={style.wrap}>
        <a href="https://brandhives.com" style={style.logo}>BrandHives</a>

        <h1 style={style.h1}>Privacy Policy</h1>
        <div style={style.updated}>Last updated: March 22, 2026</div>

        <p style={style.p}>
          BHIVE™ Social Media Labs ("BHIVE™," "we," "us," or "our") provides this Privacy Policy to inform you of our policies and procedures regarding the collection, use, protection, and disclosure of Personal Information received from your use of the BrandHives platform, located at <a href="https://brandhives.com" style={style.link}>https://brandhives.com</a> ("Site"), as well as all related websites, subdomains, applications, and other services provided by us (collectively, together with the Site, our "Service").
        </p>
        <p style={style.p}>
          This Privacy Policy also tells you about your rights and choices with respect to your Personal Information, and how you can reach us to update your contact information or get answers to questions you may have about our privacy practices.
        </p>
        <p style={style.p}>
          Registration with, use of, and access to the Service is subject to this Privacy Policy and our Terms of Use.
        </p>

        <h2 style={style.h2}>1. Personal Information We May Collect</h2>
        <p style={style.p}>
          For the purpose of this Privacy Policy, "Personal Information" means any information relating to an identified or identifiable individual. We obtain Personal Information relating to you from various sources described below.
        </p>

        <h2 style={style.h2}>2. Personal Information Provided by You</h2>

        <h3 style={style.h3}>Registration</h3>
        <p style={style.p}>
          If you desire to have access to certain restricted sections of the Site or request to receive marketing materials, you may be required to become a registered user and to submit the following types of Personal Information: your name, email address, phone number, username, password, business name, and professional title.
        </p>

        <h3 style={style.h3}>Customer Support</h3>
        <p style={style.p}>
          We may collect information through your communications with our customer support team or other communications that you may send us and their contents.
        </p>

        <h3 style={style.h3}>Making a Purchase</h3>
        <p style={style.p}>
          When you make payments through the Service, you will need to provide Personal Information such as your credit card number and billing address. Payment processing is handled by Stripe, a third-party payment processor. We do not store your full credit card details on our servers. All payment disputes, refund requests, and subscription cancellations must be initiated through your account settings at brandhives.com. We are unable to process cancellations, refunds, or billing changes made by email or other means outside the platform. Cancellation of your subscription through the platform will take effect at the end of your current billing period.
        </p>

        <h3 style={style.h3}>Connected Accounts</h3>
        <p style={style.p}>
          In order to provide presence automation services, we may ask you to connect your LinkedIn account. By connecting your LinkedIn account, you authorize BrandHives to access and use certain LinkedIn account data as described in Section 2a below.
        </p>

        <h2 style={style.h2}>2a. Personal Information Collected from LinkedIn and Other Connected Third-Party Accounts</h2>
        <p style={style.p}>
          BrandHives integrates with LinkedIn via the LinkedIn Developer API. When you connect your LinkedIn account to BrandHives, we collect and store the following information from LinkedIn, solely for the purpose of providing the Service:
        </p>
        <ul style={style.ul}>
          <li>Your LinkedIn member ID (URN)</li>
          <li>Your first and last name</li>
          <li>Your profile picture URL</li>
          <li>Your LinkedIn profile headline and industry</li>
          <li>Your LinkedIn profile URL</li>
          <li>Your approximate follower count</li>
          <li>Your email address associated with your LinkedIn account</li>
          <li>OAuth access tokens required to post on your behalf</li>
        </ul>

        <h3 style={style.h3}>How we use LinkedIn data</h3>
        <p style={style.p}>
          We use this data exclusively to generate and publish LinkedIn posts on your behalf, to personalize your content to your professional profile, and to maintain your connection to the Service. We do not sell, share, or use LinkedIn member data for advertising purposes.
        </p>

        <h3 style={style.h3}>Token storage</h3>
        <p style={style.p}>
          OAuth access tokens obtained from LinkedIn are encrypted at rest using industry-standard encryption before being stored in our database. Tokens are never logged or exposed in plaintext. We perform periodic token health checks to maintain your connection.
        </p>

        <h3 style={style.h3}>Token retention</h3>
        <p style={style.p}>
          LinkedIn access tokens are retained for as long as your account is active. Upon account cancellation or disconnection of your LinkedIn account, tokens are deleted from our systems within 30 days.
        </p>

        <h3 style={style.h3}>LinkedIn data retention</h3>
        <p style={style.p}>
          Profile data collected from LinkedIn is retained for as long as your account is active and for up to 90 days after cancellation, after which it is anonymized or deleted.
        </p>

        <h3 style={style.h3}>LinkedIn's terms</h3>
        <p style={style.p}>
          Your use of the LinkedIn integration is also subject to LinkedIn's User Agreement and Privacy Policy. We comply with LinkedIn's API Terms of Use and Developer Policy. For more information on how LinkedIn processes your data, please visit <a href="https://www.linkedin.com/legal/privacy-policy" style={style.link}>linkedin.com/legal/privacy-policy</a>.
        </p>

        <h2 style={style.h2}>2b. Personal Information Automatically Obtained from Your Interactions with the Service</h2>

        <h3 style={style.h3}>Log Data</h3>
        <p style={style.p}>
          When you use our Service, our servers automatically record information that your browser sends whenever you visit a website ("Log Data"). This Log Data may include information such as your IP address, browser type or the domain from which you are visiting, the web pages you visit, the search terms you use, and any advertisements on which you click.
        </p>

        <h3 style={style.h3}>Cookies and Similar Technologies</h3>
        <p style={style.p}>
          Like many websites, we use "cookie" technology to collect additional website usage data and to improve the Site and our Service. A cookie is a small data file that we transfer to your computer or device. You can instruct your browser to stop accepting cookies or to prompt you before accepting a cookie. Please note that if you delete or choose not to accept cookies from the Service, you may not be able to utilize the features of the Service to their fullest potential.
        </p>

        <h2 style={style.h2}>3. Artificial Intelligence and Automated Content Generation</h2>
        <p style={style.p}>
          The BrandHives platform uses artificial intelligence, including large language models provided by Anthropic (Claude) and OpenAI, to automatically generate social media content on your behalf ("AI-Generated Content").
        </p>
        <h3 style={style.h3}>Important disclaimer regarding AI-Generated Content</h3>
        <p style={style.p}>
          AI systems can and do make mistakes. AI-Generated Content may contain inaccuracies, errors, hallucinations, outdated information, or statements that are inappropriate for your specific professional context. You are solely responsible for reviewing, approving, and taking accountability for any content published to your LinkedIn profile through the Service, regardless of whether that content was AI-generated.
        </p>
        <p style={style.p}>
          BHIVE™ makes no representations or warranties regarding the accuracy, completeness, suitability, or fitness for any particular purpose of AI-Generated Content. We strongly encourage you to review content before it is published where possible by enabling the preview setting in your account.
        </p>
        <h3 style={style.h3}>AI content and your professional obligations</h3>
        <p style={style.p}>
          If you are a licensed professional — including but not limited to financial advisors, lawyers, accountants, or other regulated professionals — you are solely responsible for ensuring that any content published through the Service complies with your applicable professional standards, regulatory requirements, and governing body rules. AI-Generated Content is not a substitute for professional judgment.
        </p>

        <h2 style={style.h2}>4. Compliance Assist Feature</h2>
        <p style={style.p}>
          BrandHives offers an optional Compliance Assist feature (available on select plans) that uses automated pattern detection to flag language in AI-Generated Content that may be associated with common regulatory concerns for certain professions.
        </p>
        <h3 style={style.h3}>Important disclaimer regarding Compliance Assist</h3>
        <p style={style.p}>
          The Compliance Assist feature is provided for informational purposes only and does not constitute legal advice, compliance advice, regulatory advice, or any other form of professional advice. The feature uses automated pattern matching and AI analysis, which is inherently imperfect and may miss violations, generate false positives, or fail to account for jurisdiction-specific requirements.
        </p>
        <p style={style.p}>
          Passing the Compliance Assist check does not guarantee that any content is compliant with applicable laws, regulations, professional standards, or governing body rules. BHIVE™, its directors, officers, employees, and agents expressly disclaim any liability arising from your reliance on the Compliance Assist feature for regulatory compliance purposes.
        </p>
        <p style={style.p}>
          You are solely responsible for ensuring that all content published through the Service complies with your applicable professional and regulatory obligations. If in doubt, consult your compliance officer, legal counsel, or relevant regulatory body before publishing.
        </p>

        <h2 style={style.h2}>5. How We May Use Your Personal Information</h2>
        <p style={style.p}>We may use the Personal Information we obtain about you to:</p>
        <ul style={style.ul}>
          <li>Create and manage your account, provide our Service, process payments, and respond to your inquiries</li>
          <li>Manage account authentication and LinkedIn OAuth connections</li>
          <li>Generate and publish AI-assisted social media content on your behalf</li>
          <li>Communicate with you for informational and operational purposes, including account management, payment reminders, service notifications, and win-back communications</li>
          <li>Tailor our Service to your preferences and professional profile</li>
          <li>Aggregate your Personal Information for analytical purposes</li>
          <li>Provide customer support</li>
          <li>Operate, evaluate, and improve our business</li>
          <li>Send you marketing communications about products, services, offers, programs, and promotions of BrandHives and BHIVE™</li>
          <li>Ensure the security of our Service</li>
          <li>Comply with applicable legal requirements, industry standards, and our policies</li>
        </ul>

        <h2 style={style.h2}>6. How We Share Your Personal Information</h2>
        <h3 style={style.h3}>Service Providers</h3>
        <p style={style.p}>We engage trusted third parties to perform functions and provide services, including:</p>
        <ul style={style.ul}>
          <li>Stripe — payment processing</li>
          <li>Supabase — database storage and hosting</li>
          <li>Anthropic — AI content generation (Claude)</li>
          <li>OpenAI — AI content generation</li>
          <li>Resend — transactional email delivery</li>
          <li>Hetzner — application hosting</li>
          <li>LinkedIn — social media publishing via API</li>
        </ul>
        <p style={style.p}>We share your Personal Information with these third parties only to the extent necessary to perform these functions.</p>

        <h3 style={style.h3}>Compliance with Laws and Law Enforcement</h3>
        <p style={style.p}>
          BHIVE™ cooperates with government and law enforcement officials or private parties to enforce and comply with the law. We may disclose information about you as we believe is necessary or appropriate to investigate, respond to, and defend against claims, for legal process, or to comply with applicable law.
        </p>

        <h3 style={style.h3}>Business Transfers</h3>
        <p style={style.p}>
          BHIVE™ may share some or all of its assets, including Personal Information, in connection with a merger, acquisition, reorganization, or similar transaction.
        </p>

        <h2 style={style.h2}>7. How We Protect Your Personal Information</h2>
        <p style={style.p}>BHIVE™ employs administrative and electronic measures designed to protect your Personal Information. Specific measures include:</p>
        <ul style={style.ul}>
          <li>Encryption of OAuth access tokens at rest</li>
          <li>Encrypted database connections</li>
          <li>Row-level security on sensitive data tables</li>
          <li>No plaintext logging of credentials or tokens</li>
          <li>Regular security monitoring</li>
        </ul>
        <p style={style.p}>Please be aware that no security measures are perfect or impenetrable.</p>

        <h2 style={style.h2}>8. Your Rights and Choices</h2>
        <h3 style={style.h3}>Unsubscribe and account cancellation</h3>
        <p style={style.p}>
          If you unsubscribe from our communications at any time by clicking the unsubscribe link in any email, we will interpret this as a request to fully stop all services, including ceasing all automated LinkedIn posts on your behalf and cancelling your subscription. No further posts will be published to your LinkedIn account after you unsubscribe.
        </p>
        <p style={style.p}>
          To cancel your subscription while continuing to receive service communications, please use the cancellation option within your account settings at brandhives.com. Cancellation requests made by email or other means outside the platform cannot be processed.
        </p>
        <h3 style={style.h3}>Access and correction</h3>
        <p style={style.p}>
          In certain jurisdictions you have the right to request access and receive information about the Personal Information we maintain about you, to update and correct inaccuracies, to restrict or object to processing, or to exercise your right to data portability. To submit a request, contact us at <a href="mailto:operator@bhive.ca" style={style.link}>operator@bhive.ca</a>.
        </p>

        <h2 style={style.h2}>9. Data Transfers</h2>
        <p style={style.p}>
          BHIVE™ is based in Canada. Personal Information we collect may be transferred to and stored by our affiliates, partners, or service providers inside or outside Canada, the EEA, the UK, or Switzerland, including in the United States. When we transfer Personal Information, we protect it in accordance with this Privacy Policy and applicable data protection laws.
        </p>

        <h2 style={style.h2}>10. Data Retention and Deletion</h2>
        <p style={style.p}>We retain your Personal Information for as long as your account is active and as necessary to provide the Service. Upon account cancellation:</p>
        <ul style={style.ul}>
          <li>LinkedIn OAuth tokens are deleted within 30 days</li>
          <li>Scheduled and pending posts are cancelled immediately</li>
          <li>Your LinkedIn profile data is anonymized or deleted within 90 days</li>
          <li>Your posting history is retained in anonymized form for service improvement</li>
          <li>Your email address is retained to prevent duplicate free trial registrations</li>
        </ul>
        <p style={style.p}>
          To request complete deletion of your Personal Information, contact us at <a href="mailto:operator@bhive.ca" style={style.link}>operator@bhive.ca</a>.
        </p>

        <h2 style={style.h2}>11. Children's Privacy</h2>
        <p style={style.p}>The Service is not directed to persons under 16. We do not knowingly collect Personal Information from children under 16.</p>

        <h2 style={style.h2}>12. Updates to this Privacy Policy</h2>
        <p style={style.p}>
          This Privacy Policy may be updated from time to time. We will notify you of material modifications by posting the new Privacy Policy on our Site and indicating the date of the latest revision.
        </p>

        <h2 style={style.h2}>13. Your Canadian Privacy Rights</h2>
        <p style={style.p}>
          Under PIPEDA and applicable provincial privacy legislation, you have the right to access and correct the Personal Information we hold about you, and to withdraw consent to collection, use, or disclosure at any time, subject to legal or contractual restrictions and reasonable notice.
        </p>

        <h2 style={style.h2}>14. How to Contact Us</h2>
        <p style={style.p}>BHIVE™ Social Media Labs is the entity responsible for the processing of your Personal Information.</p>
        <p style={style.p}>
          <a href="mailto:operator@bhive.ca" style={style.link}>operator@bhive.ca</a><br />
          BHIVE™ Social Media Labs<br />
          65 Jefferson Avenue<br />
          Toronto, ON, Canada
        </p>
        <p style={{ ...style.p, color: "#888", fontSize: 13 }}>This Privacy Policy is effective as of March 22, 2026.</p>

        <div style={style.footer}>
          AI by Claude · Payments secured by Stripe · Developed by BHIVE™ Labs
        </div>
      </div>
    </div>
  );
}
